import { Component, EventEmitter, Input, OnInit, OnDestroy, Output } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { DataProvider } from 'projects/packcert-web/src/app/providers/data.provider';
import { Message, SpinnerService } from 'shared-components';
import { AdminService } from '../../../services/admin.service';
import { SessionContextService } from 'projects/packcert-web/src/app/context/session-context.service';
import { TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
    selector: 'app-mantenimiento-ec-new-edit',
    templateUrl: './new-edit.component.html'
})
export class MantenimientoEntidadesControlNewEditComponent implements OnInit, OnDestroy {

    public newEditForm: UntypedFormGroup;
    public showError: boolean;
    public mensajes = new Array<Message>();

    private readonly destroy$ = new Subject<void>();

    @Input() entidadControl?;

    @Output() refresh = new EventEmitter<boolean>();
    @Output() mensaje = new EventEmitter<string>();

    constructor(
        private _fb: UntypedFormBuilder,
        private readonly _dataProvider: DataProvider,
        private readonly _spinnerService: SpinnerService,
        private readonly _adminService: AdminService,
        private readonly _translate: TranslateService,
        private readonly _sessionContext: SessionContextService
    ) {}

    ngOnInit() {
        this.newEditForm = this._fb.group({
            des: ['', Validators.required],
            bloqueoAutomaticoCertificado: ['', Validators.required],
            blockPercentage: [0, Validators.pattern(/^\d*$/)],
            menuExpedicion: [false]
        });

        this.newEditForm.get('bloqueoAutomaticoCertificado')?.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(value => {
          if (value && value.value) {
            this.newEditForm.get('blockPercentage')?.enable();
          } else {
            this.newEditForm.get('blockPercentage')?.disable();
          }
        });

        const blockOption = this.bloqueoPorcentajes?.find(option => option.value);
        if (this.entidadControl) {
            this.newEditForm.patchValue({
                des: this.entidadControl.des,
                bloqueoAutomaticoCertificado: this.bloqueoPorcentajes?.find(option => option.value === this.entidadControl?.bloqueoAutomaticoCertificadoExpdicion) || blockOption,
                blockPercentage: this.entidadControl?.porcentajeCertExpBloqAuto || 0,
                menuExpedicion: this.entidadControl.mostrarMenuExpedicion
            });
        } else {
            this.newEditForm.patchValue({
                bloqueoAutomaticoCertificado: blockOption,
            });

        }
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    public close() {
        this.refresh.emit(false);
    }

    public save() {
        this.checkForm();
        if (this.newEditForm.valid && !this.entidadControl) {
            const filters = {
                des: this.newEditForm.get('des').value,
                porcentajeCertExpBloqAuto: this.newEditForm.get('blockPercentage')?.value ? Number(this.newEditForm.get('blockPercentage')?.value) : 0,
                mostrarMenuExpedicion: this.newEditForm.get('menuExpedicion').value,
                bloqueoAutomaticoCertificadoExpdicion:
                    this.newEditForm.get("bloqueoAutomaticoCertificado")?.value?.value,
            };
            this._spinnerService.showSpinner();
            this._adminService.createEntidadControl(filters).toPromise().finally(() => {
                this._spinnerService.hideSpinner();
            }).then((resp) => {
                if (resp) {
                this._dataProvider.refreshEntidadControl();
                  this.refresh.emit(true);
                  this.mensaje.emit('global.success.itemCreated');
                } else {
                  this.showError = true;
                  this.mensajes.push({ class: 'error', message: 'global.error.noResponse' });
                }
            },
            (error) => {
                this.showError = true;
                switch (error.error.ApiErrorCode) {
                  case 3000: //FuncionalidadRestringida
                    this.mensajes.push({ class: 'error', message: 'ApiErrors.funcionalidadRestringida' });
                    break;
                  default:
                    this.mensajes.push({ class: 'error', message: 'global.error.noResponse' });
                    break;
                }
            });
        } else if (this.newEditForm.valid && this.entidadControl) {
            const filters = {
                id: this.entidadControl.id,
                des: this.newEditForm.get('des').value,
                porcentajeCertExpBloqAuto: this.newEditForm.get('blockPercentage')?.value ? Number(this.newEditForm.get('blockPercentage')?.value) : 0,
                mostrarMenuExpedicion: this.newEditForm.get('menuExpedicion').value,
                bloqueoAutomaticoCertificadoExpdicion:
                    this.newEditForm.get("bloqueoAutomaticoCertificado")?.value?.value,
            };
            this.showError = false;
            this.mensajes = [];
            this._spinnerService.showSpinner();
            this._adminService.updateEntidadControl(filters).toPromise().finally(() => {
                this._spinnerService.hideSpinner();
            }).then((resp) => {
                this._dataProvider.refreshEntidadControl();
                this.refresh.emit(true);
                this.mensaje.emit('global.success.itemUpdated');
            },
            (error) => {
                this.showError = true;
                switch (error.error.ApiErrorCode) {
                  case 3000: //FuncionalidadRestringida
                    this.mensajes.push({ class: 'error', message: 'ApiErrors.funcionalidadRestringida' });
                    break;
                  case 6142: //EntidadControlNoAccesible
                    this.mensajes.push({ class: 'error', message: 'ApiErrors.entidadControlNoAccesible' });
                    break;
                  default:
                    this.mensajes.push({ class: 'error', message: 'global.error.noResponse' });
                    break;
                }
            });
        }
    }

    private checkForm() {
        this.showError = this.newEditForm.invalid;
        if (this.showError) {
            const emptyFields = Object.values(this.newEditForm.controls).some(
                control => control.errors?.['required']
            );

            if (emptyFields) {
                this.mensajes.push({ class: 'error', message: 'global.error.required' });
            }

            if (this.newEditForm.get('blockPercentage').invalid) {
               this.mensajes.push({ class: 'error', message: 'mantenimiento-entidades-control.error.percentageFormat' }); 
            }
        }
    }
    
    public get isSmallScreen() {
        return this._sessionContext.isSmallScreen();
    }
    
    public get bloqueoPorcentajes() {
        return this._dataProvider.bloqueoPorcentajes;
    }
}
